/**
 * Anonymous-handle ownership proofs, generated in the browser.
 *
 * `hsk` is the handle's ownership secret: whoever holds it can rotate the
 * record and repoint the handle at their own keys. Sending it to a prover
 * service hands that power over, so the witness is never transmitted — the
 * proof is produced locally from published circuit artifacts.
 */

import { generateProofClientSide } from "./proving";

export type HandleProofAction = "register" | "rotate";

export interface HandleProofWitness {
  handleId: string;
  recordCommitment: string;
  ownerCommitment: string;
  nullifier: string;
  handleLabelHash: string;
  recordHash: string;
  hsk: string;
  epoch: number;
  chainId: number;
}

const ACTION_TAG: Record<HandleProofAction, number> = {
  register: 1,
  rotate: 2,
};

function toDecimal(value: string): string {
  return BigInt(value).toString();
}

/**
 * Groth16 proof in the `uint256[8]` layout the EVM adapter and the on-chain
 * program expect: [pA, pB.x, pB.y, pC], with each G2 coordinate pair in
 * EIP-197 order rather than the order snarkjs emits.
 */
export async function proveHandleOwnership(
  action: HandleProofAction,
  witness: HandleProofWitness,
  artifactsBaseUrl?: string,
): Promise<string[]> {
  const { proof } = await generateProofClientSide(
    `handle_${action}`,
    {
      handleId: toDecimal(witness.handleId),
      recordCommitment: toDecimal(witness.recordCommitment),
      ownerCommitment: toDecimal(witness.ownerCommitment),
      epoch: String(witness.epoch),
      nullifier: toDecimal(witness.nullifier),
      chainId: String(witness.chainId),
      actionTag: String(ACTION_TAG[action]),
      handleLabelHash: toDecimal(witness.handleLabelHash),
      recordHash: toDecimal(witness.recordHash),
      hsk: toDecimal(witness.hsk),
    },
    artifactsBaseUrl,
  );

  const groth16 = proof as {
    pi_a?: string[];
    pi_b?: string[][];
    pi_c?: string[];
  };
  // snarkjs emits each G2 coordinate as [c0, c1]; the pairing precompile the
  // generated verifier calls reads them EIP-197 order, [c1, c0]. The inner
  // pairs are swapped here for the same reason they are in the server-side
  // prover once did; perps-ownership-prover-client.ts applies the same swap.
  const flattened = [
    groth16.pi_a?.[0],
    groth16.pi_a?.[1],
    groth16.pi_b?.[0]?.[1],
    groth16.pi_b?.[0]?.[0],
    groth16.pi_b?.[1]?.[1],
    groth16.pi_b?.[1]?.[0],
    groth16.pi_c?.[0],
    groth16.pi_c?.[1],
  ];
  if (flattened.some((value) => value === undefined || value === null)) {
    throw new Error(`Handle ${action} proof has an unexpected shape`);
  }
  return flattened.map((value) => toDecimal(String(value)));
}
